import React, { useState, useEffect, useRef } from "react";
import { parseDate, getLocalTimeZone } from "@internationalized/date";
import DatePickerField from "../../../common/DatePickerDashboard";
import NoDataFound from "../../../common/NoDataFound";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPolicyTypeFilteredDataAction,
  fetchAllPolicyTypeAction,
} from "../../../redux/actions/dashboardAction";
import {
  Box,
  Button,
  Typography,
  FormControl,
  Select,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import Loader from "../../loader/loader";
import { useNavigate } from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

import ReusableTable from "../../../common/ReusableTable";

const DashboardPolicyType = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [fromDate, setFromDate] = useState(dayjs().startOf("month"));
  const [toDate, setToDate] = useState(dayjs());
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [isFiltered, setIsFiltered] = useState(false);

  const filteredPolicyData = useSelector(
    (state) => state?.dashboardData?.all_policy_type?.policiesPage?.content || []
  );

  const navigate = useNavigate();
  const [fieldErrors, setFieldErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  const getToday = () => {
    const today = new Date();
    return parseDate(
      `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(
        today.getDate()
      ).padStart(2, "0")}`
    );
  };

  const getFirstOfMonth = () => {
    const today = new Date();
    return parseDate(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-01`);
  };

  const formatForForm = (date) => {
    const d = date.toDate(getLocalTimeZone());
    return `${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(
      2,
      "0"
    )}-${d.getFullYear()}`;
  };

  const initialFormData = {
    fromDate: formatForForm(getFirstOfMonth()),
    toDate: formatForForm(getToday()),
  };

  const [formData, setFormData] = useState(initialFormData);

  const handleDateChange = (fieldName, value) => {
    setFormData((prev) => ({ ...prev, [fieldName]: value }));
  };

  const handleBlur = (fieldName, errorMsg) => {
    setTouchedFields((prev) => ({ ...prev, [fieldName]: true }));
    setFieldErrors((prev) => ({ ...prev, [fieldName]: errorMsg }));
  };

  const didRunRef = useRef(false);
  // useEffect(() => {
  //   if (isFiltered) return;

  //   if (process.env.NODE_ENV === "development") {
  //     if (didRunRef.current) return;
  //     didRunRef.current = true;
  //   }

  //   dispatch(fetchAllPolicyTypeAction(page, rowsPerPage));
  // }, [dispatch, page, rowsPerPage, isFiltered]);

  const allPolicy = useSelector(
    (state) => state?.dashboardData?.all_policy_type?.policiesPage?.content || []
  );

  const { loading, error } = useSelector((state) => state.dashboardData);

  const paginatedData = useSelector((state) => {
    return state.dashboardData?.all_policy_type?.policiesPage || {};
  });

  const displayData = isFiltered ? filteredPolicyData : allPolicy;

  const handleApply = () => {
    const payload = {
      fromDate: dayjs(formData.fromDate).format("MM-DD-YYYY") || "",
      toDate: dayjs(formData.toDate).format("MM-DD-YYYY") || "",
      policyType: selectedFilter,
    };

    if ((fromDate && toDate) || selectedFilter !== "All") {
      dispatch(fetchPolicyTypeFilteredDataAction(payload, page, rowsPerPage));
      setIsFiltered(true);
    } else {
      dispatch(fetchAllPolicyTypeAction(page, rowsPerPage));
      setIsFiltered(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (isFiltered) {
      const payload = {
        fromDate: dayjs(formData.fromDate).format("MM-DD-YYYY") || "",
        toDate: dayjs(formData.toDate).format("MM-DD-YYYY") || "",
        policyType: selectedFilter,
      };
      dispatch(fetchPolicyTypeFilteredDataAction(payload, newPage, rowsPerPage));
    } else {
      dispatch(fetchAllPolicyTypeAction(newPage, rowsPerPage));
    }
  };

  const formatPhoneNumber = (number) => {
    if (!number) return "";
    const cleaned = ("" + number).replace(/\D/g, "");
    if (cleaned.length === 11 && cleaned.startsWith("1")) {
      return cleaned.slice(1).replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
    }
    if (cleaned.length === 10) {
      return cleaned.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
    }
    return number;
  };

  const handleRowClick = (rowData) => {
    const formattedData = {
      id: rowData.id,
      firstName: rowData.name?.split(" ")[0] || "",
      middleName: null,
      lastName: rowData.name?.split(" ")[1] || "",
      email: rowData.email || null,
      policyNumber: rowData.policyNumber,
      policyType: rowData.policyType,
      dateOfBirth: null,
      phoneNumber: rowData.phoneNumber,
      company: rowData.company,
      licenseNumber: null,
      effectiveDate: rowData.effectiveDate,
      expiryDate: rowData.expiryDate,
      status: rowData.status,
      lineOfBusiness: rowData.lineOfBusiness,
      premium: rowData.premium,
    };

    navigate("/globalsearch", {
      state: {
        id: formattedData.id,
        showPolicyDetails: true,
        clientCustomerId: rowData.clientCustomerId,
        selectedPolicyData: rowData,
      },
    });
    dispatch({ type: "CLEAR_GET_CLIENT_SUCCESS" });
    dispatch({ type: "CLEAR_GET_CUSTOMER_DETAILS_SUCCESS" });
    dispatch({ type: "CLEAR_CUSTOMER_SEARCH_LIST_SUCCESS" });
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    if (isFiltered) {
      const payload = {
        fromDate: dayjs(formData.fromDate).format("MM-DD-YYYY") || "",
        toDate: dayjs(formData.toDate).format("MM-DD-YYYY") || "",
        policyType: selectedFilter,
      };
      dispatch(fetchPolicyTypeFilteredDataAction(payload, 0, newRowsPerPage));
    } else {
      dispatch(fetchAllPolicyTypeAction(0, newRowsPerPage));
    }
  };

  const handleFilterChange = (event) => {
    setSelectedFilter(event.target.value);
  };

  const isApplyButtonDisabled = !(fromDate && toDate) && selectedFilter === "All";

  const handleReset = () => {
    setFromDate(dayjs().startOf("month"));
    setToDate(dayjs());
    setFormData({
      fromDate: formatForForm(getFirstOfMonth()),
      toDate: formatForForm(getToday()),
    });
    setSelectedFilter("All");
    setIsFiltered(false);
    setPage(0);
    setFieldErrors({});
    setTouchedFields({});
    setRowsPerPage(5);
    dispatch(fetchAllPolicyTypeAction(0, rowsPerPage));
  };

  const columnsPolicyType = [
    { id: "policyNumber", label: "Policy Number", field: "policyNumber" },
    {
      id: "phoneNumber",
      label: "Phone Number",
      render: (row) => formatPhoneNumber(row.phoneNumber),
    },
    { id: "policyType", label: "Policy Type", field: "policyType" },
    { id: "company", label: "Company", field: "company" },
    { id: "lob", label: "Line of Business", field: "lineOfBusiness" },
    { id: "eff", label: "Effective Date", field: "effectiveDate" },
    { id: "exp", label: "Expiration Date", field: "expirationDate" },
    { id: "status", label: "Status", field: "status" },
    {
      id: "premium",
      label: "Premium",
      align: "right",
      render: (row) => row.premium,
    },
  ];

  return (
    <Box>
      <Box
        sx={{
          padding: isMobile ? "8px" : "10px",
          backgroundColor: "#ffffff",
          overflowX: "auto",
          minHeight: "40vh",
          "@media (min-width: 768px) and (max-width: 1024px)": {
            minHeight: "100vh",
            height: "auto",
            overflowY: "auto",
          },
          "@media (min-width: 1024px) and (min-height: 1366px)": {
            height: "auto",
            minHeight: "70vh",
          },
        }}
      >
        <Box mb={3} p={2} sx={{ border: '1px solid #e0e0e0', borderRadius: '8px' }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: 2,
            alignItems: "flex-start",
            marginBottom: "12px",
            flexWrap: "wrap",
            backgroundColor: "white",
            borderRadius: "8px",
          }}
        >
          {/* From Date */}
          <Box
            sx={{
              width: isMobile ? "100%" : "215px",
              minWidth: isMobile ? "100%" : "215px",
              minHeight: "85px",
            }}
          >
            <Typography variant="subtitle2" sx={{ marginBottom: "4px" }}>
              From Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePickerField
                fieldName="fromDate"
                formData={formData}
                handleDateChange={handleDateChange}
                handleBlur={handleBlur}
                touchedFields={touchedFields}
                fieldErrors={fieldErrors}
                size={isMobile ? "small" : "medium"}
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root": {
                    height: "55px",
                  },
                }}
                inputProps={{
                  id: "fromDate",
                  name: "fromDate",
                }}
              />
            </LocalizationProvider>
          </Box>

          <Box
            sx={{
              width: isMobile ? "100%" : "215px",
              minWidth: isMobile ? "100%" : "215px",
              minHeight: "85px",
            }}
          >
            <Typography variant="subtitle2" sx={{ marginBottom: "4px" }}>
              To Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePickerField
                fieldName="toDate"
                formData={formData}
                handleDateChange={handleDateChange}
                handleBlur={handleBlur}
                touchedFields={touchedFields}
                fieldErrors={fieldErrors}
                size={isMobile ? "small" : "medium"}
                sx={{
                  width: "100%",
                  "& .MuiInputBase-root": {
                    height: "55px",
                  },
                }}
                inputProps={{
                  id: "toDate",
                  name: "toDate",
                }}
              />
            </LocalizationProvider>
          </Box>

          <FormControl
            sx={{
              width: isMobile ? "100%" : "200px",
              minWidth: isMobile ? "100%" : "200px",
              minHeight: "85px",
            }}
          >
            <Typography variant="subtitle2" sx={{ marginBottom: "4px" }}>
              Policy Type
            </Typography>
            <Select
              native
              value={selectedFilter}
              onChange={handleFilterChange}
              size={isMobile ? "small" : "medium"}
              sx={{
                width: "100%",
                height: "55px",
                "& .MuiSelect-select": {
                  display: "flex",
                  alignItems: "center",
                  height: "100% !important",
                },
              }}
              inputProps={{
                name: "policyType",
                id: "policytype",
              }}
            >
              <option value="All">All</option>
              <option value="New Business">New Business</option>
              <option value="Renewal">Renewal</option>
              <option value="Rewrite">Rewrite</option>
            </Select>
          </FormControl>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              width: isMobile ? "100%" : "auto",
              minHeight: "85px",
              alignItems: "flex-end",
              marginTop: "-3px",
              "& button": {
                flex: isMobile ? 1 : "none",
                height: "55px",
                minHeight: "55px",
              },
            }}
          >
            <Button
              variant="contained"
              id="apply-button"
              onClick={handleApply}
              size={isMobile ? "small" : "medium"}
             
              disabled={isApplyButtonDisabled}
            >
              Apply
            </Button>
            <Button
              onClick={handleReset}
              size={isMobile ? "small" : "medium"}
              variant="outlined"
              id="apply-reset"
            >
              Reset
            </Button>
          </Box>
        </Box>
        </Box>
         
          {!isFiltered ? (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '200px',
      backgroundColor: '#f9f9f9',
      borderRadius: 2,
      border: '1px solid #e0e0e0',
    }}
  >
    <NoDataFound
      discription="Please use the search filters above to fetch and display updated Policy Type data."
      message="After click Apply- results will shown as per selected filter"
    />
  </Box>
) : displayData.length === 0 ? (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '200px',
      backgroundColor: '#f9f9f9',
      borderRadius: 2,
      border: '1px solid #e0e0e0',
    }}
  >
    <NoDataFound
      title="No Data Found"
      discription="Please change search filters range where data is available as per defined range"
      message="After click Apply- results will shown as per selected filter"
    />
  </Box>
) : (
  <ReusableTable
    columns={columnsPolicyType}
    rows={displayData}
    loading={loading}
    error={error}
    LoaderComponent={Loader}
    page={page}
    rowsPerPage={rowsPerPage}
    rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
    totalPages={paginatedData?.totalPages || 0}
    totalElements={paginatedData?.totalElements || 0}
    onPageChange={(next) => handleChangePage(null, next)}
    onRowsPerPageChange={handleChangeRowsPerPage}
    getRowId={(row) => row.id}
    onRowClick={(row) => handleRowClick(row)}
    size={isMobile ? "small" : "medium"}
    tableSx={{
      minWidth: isMobile ? "100%" : "650px",
    }}
    containerSx={{
      maxHeight: isMobile ? "60vh" : "none",
      overflow: "auto",
      width: "100%",
    }}
  />
)}
      </Box>
    </Box>
  );
};

export default DashboardPolicyType;
